import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { List, Tag, Pagination, Modal, Drawer, Spin, message, Button } from 'antd'
import EzvizPlayer from '@/components/EzvizPlayer'
import NursingAlertsForm from '@/views/commandCenter/share/NursingAlertsForm'
import { renderTextStr, formatDateTimeStr } from '@/utils/renderUtils'
import { ALERT_LEVEL_CONFIG } from '@/views/commandCenter/config'
import {
  getSeeData,
  getDevicePart,
  normalizeChannelNo,
  getStreamKey,
  getYsLiveParamsFromGroup,
  toSafeDomIdPart,
  getMonitoringThumbnailUrl,
  getMonitoringSnapshotPreviewUrls,
  getMonitoringAlertSummary,
  isSevereMonitoringLevel,
  getEventTimeMs,
  sortSseItemsByTimeDesc,
  groupsToVirtualSseItems,
  getMonitoringIsProcessed,
  pickLatestMonitoringItem,
  isMonitoringAlertPayload
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'

const PAGE_SIZE = 20

const EMPTY_PROCESSED_STREAM_KEYS = {}

const STREAM_KEY_SEP = '\u0001'

/** streamKey → 解除提示用短文案（与列表 Tag 口径一致） */
function streamKeyToProcessedToastLabel(sk) {
  if (!sk || typeof sk !== 'string') return ''
  const [deviceRaw, chRaw] = sk.split(STREAM_KEY_SEP)
  const deviceLabel =
    !deviceRaw || deviceRaw === '_unknown' ? '未知设备' : renderTextStr(deviceRaw)
  const ch = chRaw == null || chRaw === '' ? '_' : chRaw
  const channelLabel = ch === '_' ? '未标注' : renderTextStr(ch)
  return `${deviceLabel} · 通道 ${channelLabel}`
}

/** 直播弹窗：在视口内 16:9 铺满，宽度不超过 1200，随 resize 更新 */
function computeLiveModalLayout() {
  if (typeof window === 'undefined') {
    return { modalWidth: 880, playerHeight: 495 }
  }
  const vw = window.innerWidth
  const vh = window.innerHeight
  const maxModalW = Math.min(1200, vw - 32)
  const bodyPadX = 48
  const chromeY = 128
  const maxPlayerH = Math.max(180, vh - chromeY)
  let innerW = maxModalW - bodyPadX
  let playerH = (innerW * 9) / 16
  if (playerH > maxPlayerH) {
    playerH = maxPlayerH
    innerW = (playerH * 16) / 9
  }
  const modalW = Math.max(300, Math.round(innerW + bodyPadX))
  const innerW2 = modalW - bodyPadX
  const h = Math.min((innerW2 * 9) / 16, maxPlayerH)
  return { modalWidth: modalW, playerHeight: Math.max(160, Math.round(h)) }
}

/**
 * 监控预警：按设备 + 通道聚合
 *
 * 数据流：1）先由父级 getMonitoringGroupUnprocessed 写入 Redux 并转为虚拟项「初始化」列表；
 * 2）再仅由 SSE 分桶 `monitoring_alarm` 增量与 HTTP 项合并，驱动 renderGroupRow。
 * 3）SSE `monitoring_alarm_processed` 在 Redux 中记下 streamKey，本面板合并列表时过滤掉该路。
 * 见：@/redux/sse getMonitoringAlarmSseList、getMonitoringAlarmProcessedStreamKeysMap
 */
function MonitoringSseAlertsPanel({
  /** state.alerts.monitoringGroupUnprocessed：groups / loading / loaded */
  monitoringGroupUnprocessed,
  /** 仅 byType.monitoring_alarm，勿传合并后的 message 分桶 */
  monitoringAlarmSseMessages,
  /** state.sse.data.monitoringAlarmProcessedStreamKeys：已处理（解除）的设备+通道 */
  monitoringAlarmProcessedStreamKeys,
  /** 可覆盖由 groups 转出的 Java 项（单测/调试） */
  javaVirtualMessages: javaVirtualMessagesProp,
  getStatusConfig,
  monitoringServiceQuery,
  ys7AccessToken
}) {
  const [page, setPage] = useState(1)
  const [liveModal, setLiveModal] = useState(null)
  const [liveModalLayout, setLiveModalLayout] = useState(() => computeLiveModalLayout())
  /** 预警截图预览：urls 为当前路最新一条的多张截帧 */
  const [snapshotPreview, setSnapshotPreview] = useState(null)
  /** 本路明细抽屉挂在面板级，避免列表随 SSE 刷新时子树重挂载导致抽屉被关掉 */
  const [monitoringDrawer, setMonitoringDrawer] = useState(null)
  /** 每次打开弹窗换新 id，避免 EZUIKit 与旧 DOM 冲突 */
  const liveModalDomKeyRef = useRef(0)
  /** 用于对比「新出现的已解除 key」，首帧不提示 */
  const prevProcessedKeysRef = useRef(undefined)

  const updateLiveModalLayout = useCallback(() => {
    setLiveModalLayout(computeLiveModalLayout())
  }, [])

  useEffect(() => {
    if (!liveModal) return undefined
    updateLiveModalLayout()
    window.addEventListener('resize', updateLiveModalLayout)
    return () => window.removeEventListener('resize', updateLiveModalLayout)
  }, [liveModal, updateLiveModalLayout])

  const openLiveModal = useCallback((params) => {
    if (!params?.deviceSerial || !ys7AccessToken) return
    liveModalDomKeyRef.current += 1
    setLiveModal({
      ...params,
      containerId: `sse-live-modal-${liveModalDomKeyRef.current}`
    })
  }, [ys7AccessToken])

  const closeLiveModal = useCallback(() => setLiveModal(null), [])

  const openSnapshotPreview = useCallback((payload) => {
    const urls = Array.isArray(payload) ? payload : payload?.urls
    const list = Array.isArray(urls) ? urls.filter((u) => typeof u === 'string' && u.trim()) : []
    if (list.length === 0) {
      message.info('暂无预警截图')
      return
    }
    setSnapshotPreview({
      urls: list,
      streamKey: payload?.streamKey ?? null,
      thumbUrl: payload?.thumbUrl ?? list[0]
    })
  }, [])

  const closeSnapshotPreview = useCallback(() => setSnapshotPreview(null), [])

  const openMonitoringDrawer = useCallback((payload) => {
    if (payload != null && typeof payload === 'object' && !Array.isArray(payload)) {
      setMonitoringDrawer(payload)
      return
    }
    setMonitoringDrawer({ streamKey: payload, titlePrefix: '' })
  }, [])

  const closeMonitoringDrawer = useCallback(() => setMonitoringDrawer(null), [])

  const processedKeyMap = monitoringAlarmProcessedStreamKeys ?? EMPTY_PROCESSED_STREAM_KEYS

  useEffect(() => {
    const sk = monitoringDrawer?.streamKey
    if (sk && processedKeyMap[sk]) closeMonitoringDrawer()
  }, [monitoringDrawer?.streamKey, monitoringAlarmProcessedStreamKeys, closeMonitoringDrawer])

  /** SSE 宣告已解除时轻提示（仅增量 key，避免首屏/还原 state 刷屏） */
  useEffect(() => {
    const cur = monitoringAlarmProcessedStreamKeys
    if (!cur || typeof cur !== 'object') {
      prevProcessedKeysRef.current = cur
      return
    }
    const prev = prevProcessedKeysRef.current
    if (prev === undefined) {
      prevProcessedKeysRef.current = { ...cur }
      return
    }
    for (const sk of Object.keys(cur)) {
      if (!Object.prototype.hasOwnProperty.call(prev, sk)) {
        const label = streamKeyToProcessedToastLabel(sk)
        message.success({
          content: label ? `预警已解除：${label}` : '该路监控预警已解除',
          duration: 4
        })
      }
    }
    prevProcessedKeysRef.current = { ...cur }
  }, [monitoringAlarmProcessedStreamKeys])

  const httpInitLoading = Boolean(
    monitoringGroupUnprocessed && monitoringGroupUnprocessed.loading
  )

  useEffect(() => {
    if (monitoringGroupUnprocessed?.loaded) {
      setPage(1)
    }
  }, [monitoringGroupUnprocessed?.loaded])

  const javaVirtualFromRedux = useMemo(
    () => groupsToVirtualSseItems(monitoringGroupUnprocessed?.groups),
    [monitoringGroupUnprocessed?.groups]
  )
  const javaVirtualMessages = useMemo(() => {
    if (javaVirtualMessagesProp !== undefined) return javaVirtualMessagesProp
    return javaVirtualFromRedux
  }, [javaVirtualMessagesProp, javaVirtualFromRedux])

  /**
   * HTTP 初始化项铺底，后接 monitoring_alarm 推送（同 streamKey 聚合成组）；已 processed 的路剔除。
   * SSE 安全包不算预警（与监控墙同源），HTTP 未处理基线按接口口径原样铺底。
   */
  const mergedMessages = useMemo(() => {
    const sseAlerts = (monitoringAlarmSseMessages || []).filter(
      (item) => isMonitoringAlertPayload(getSeeData(item))
    )
    const list = [...(javaVirtualMessages || []), ...sseAlerts]
    const keys = processedKeyMap
    if (!keys || typeof keys !== 'object') return list
    return list.filter((item) => !keys[getStreamKey(item)])
  }, [javaVirtualMessages, monitoringAlarmSseMessages, processedKeyMap])

  const monitoringDrawerItems = useMemo(() => {
    const sk = monitoringDrawer?.streamKey
    if (!sk) return []
    return sortSseItemsByTimeDesc(
      (mergedMessages || []).filter((m) => getStreamKey(m) === sk)
    )
  }, [mergedMessages, monitoringDrawer?.streamKey])

  const deviceGroups = useMemo(() => {
    const list = mergedMessages || []
    const map = new Map()
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i]
      const seeData = getSeeData(item)
      const streamKey = getStreamKey(item)
      if (!map.has(streamKey)) {
        map.set(streamKey, {
          streamKey,
          devicePart: getDevicePart(seeData),
          channelNo: normalizeChannelNo(seeData),
          items: []
        })
      }
      const g = map.get(streamKey)
      g.items.push(item)
    }
    for (const g of map.values()) {
      g.latestItem = pickLatestMonitoringItem(g.items)
      g.latestTime = g.latestItem ? getEventTimeMs(getSeeData(g.latestItem)) : 0
    }
    return Array.from(map.values()).sort((a, b) => {
      const aSevere = isSevereMonitoringLevel(getSeeData(a.latestItem))
      const bSevere = isSevereMonitoringLevel(getSeeData(b.latestItem))
      if (aSevere !== bSevere) return aSevere ? -1 : 1
      return b.latestTime - a.latestTime
    })
  }, [mergedMessages])

  const total = deviceGroups.length

  const pagedList = useMemo(
    () => deviceGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [deviceGroups, page]
  )

  /** 开发态：查「初始化没数据」、以及 httpInitLoading 为 true 时 List 不挂载导致 renderGroupRow 不跑 */
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return
    // eslint-disable-next-line no-console
    console.log('[MonitoringSseAlertsPanel] 数据快照', {
      http: {
        loading: monitoringGroupUnprocessed?.loading,
        loaded: monitoringGroupUnprocessed?.loaded,
        error: monitoringGroupUnprocessed?.error,
        groups: monitoringGroupUnprocessed?.groups,
        groupsLength: monitoringGroupUnprocessed?.groups?.length
      },
      javaVirtualCount: (javaVirtualMessages || []).length,
      monitoringAlarmSseCount: (monitoringAlarmSseMessages || []).length,
      mergedCount: (mergedMessages || []).length,
      deviceGroupCount: deviceGroups.length,
      page,
      pagedListLength: pagedList.length,
      httpInitLoading,
      说明: httpInitLoading
        ? '请求中不渲染 List，renderGroupRow 不会执行；请求结束后才有行'
        : 'List 已渲染，若有组则会调用 renderGroupRow'
    })
  }, [
    monitoringGroupUnprocessed,
    javaVirtualMessages,
    monitoringAlarmSseMessages,
    mergedMessages,
    deviceGroups,
    page,
    pagedList,
    httpInitLoading
  ])

  const renderDeviceLabel = (key) => (key === '_unknown' ? '未知设备' : renderTextStr(key))

  const renderChannelLabel = (ch) => (ch === '_' ? '未标注' : renderTextStr(ch))

  const renderGroupRow = (group) => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      // console.log('[MonitoringSseAlertsPanel] renderGroupRow', {
      //   streamKey: group?.streamKey,
      //   itemCount: group?.items?.length,
      //   devicePart: group?.devicePart,
      //   channelNo: group?.channelNo,
      //   latestId: group?.latestItem?.id,
      //   latest: group?.latestItem
      // })
    }
    const latest = group.latestItem
    if (!latest) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('[MonitoringSseAlertsPanel] renderGroupRow: 无 latestItem，跳过渲染', group)
      }
      return null
    }
    const seeData = getSeeData(latest)
    const isSevereRow = isSevereMonitoringLevel(seeData)
    const isProcessed = getMonitoringIsProcessed(latest)
    const statusConfig =
      typeof isProcessed === 'boolean'
        ? {
            text: isProcessed ? '已处理' : '未处理',
            color: isProcessed ? 'success' : 'error',
            tagStyle: { marginRight: 8 }
          }
        : getStatusConfig(latest.status)
    const alertSummary = getMonitoringAlertSummary(seeData)
    const deviceLabel = renderDeviceLabel(group.devicePart)
    const channelLabel = renderChannelLabel(group.channelNo)
    const liveParams = getYsLiveParamsFromGroup(group)
    const groupRowKey = toSafeDomIdPart(group.streamKey)
    const previewThumbSrc = getMonitoringThumbnailUrl(seeData)
    const previewModalUrls = getMonitoringSnapshotPreviewUrls(seeData)
    const drawerTitlePrefix = `${deviceLabel} · 通道${channelLabel} 本路全部`
    const timeMs = getEventTimeMs(seeData)
    const lv = seeData.level
    const levelValue =
      lv && typeof lv === 'object' && !Array.isArray(lv)
        ? Number(lv.value)
        : Number(lv)
    const levelCfg =
      Number.isFinite(levelValue) && levelValue > 0 && ALERT_LEVEL_CONFIG[levelValue]
        ? ALERT_LEVEL_CONFIG[levelValue]
        : null
    // 与护理/产康同一套：1关注 2提醒 3紧急 4特急；不再用接口 desc（低/中/高/严重）
    const levelText = levelCfg ? levelCfg.text : null

    return (
      <List.Item
        key={`sse-grp-${groupRowKey}`}
        className={isSevereRow ? 'alert-list-item alert-list-item--monitoring-severe' : 'alert-list-item'}
      >
        <div className="monitoring-sse-panel__row">
          <div className="monitoring-sse-panel__main">
            <div className="clickable-container">
              <div
                role="button"
                tabIndex={0}
                className="full-width"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation()
                  openMonitoringDrawer({
                    streamKey: group.streamKey,
                    titlePrefix: drawerTitlePrefix,
                    deviceId: group.devicePart === '_unknown' ? undefined : group.devicePart,
                    channelNo: group.channelNo === '_' ? '1' : String(group.channelNo)
                  })
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    openMonitoringDrawer({
                      streamKey: group.streamKey,
                      titlePrefix: drawerTitlePrefix,
                      deviceId: group.devicePart === '_unknown' ? undefined : group.devicePart,
                      channelNo: group.channelNo === '_' ? '1' : String(group.channelNo)
                    })
                  }
                }}
              >
                <div className="tag-container">
                  <div>
                    <Tag color="blue">设备: {deviceLabel}</Tag>
                    <Tag color="cyan">通道: {channelLabel}</Tag>
                    <Tag color="purple">本路 {group.items.length} 条</Tag>
                    {levelText ? (
                      <Tag color={levelCfg ? levelCfg.color : 'magenta'} style={levelCfg?.tagStyle}>
                        等级: {levelText}
                      </Tag>
                    ) : null}
                    <Tag color={statusConfig.color} style={statusConfig.tagStyle}>
                      {statusConfig.text}
                    </Tag>
                  </div>
                  {alertSummary ? (
                    <div className="type-container" style={{ marginTop: 4, whiteSpace: 'normal' }}>
                      <span>
                        预警信息:{' '}
                        <span className="type-value" style={{ lineHeight: 1.45 }}>
                          {renderTextStr(alertSummary)}
                        </span>
                      </span>
                    </div>
                  ) : null}
                  <span className="time-text">
                    最近时间: {timeMs > 0 ? formatDateTimeStr(timeMs) : '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {liveParams && ys7AccessToken && (
            <div className="monitoring-sse-panel__thumb-col">
              <div className="monitoring-sse-panel__thumb-frame">
                {previewThumbSrc ? (
                  <>
                    <img src={previewThumbSrc} alt="预警画面截帧" className="monitoring-sse-panel__thumb-img" />
                    {previewModalUrls.length > 1 && (
                      <span className="monitoring-sse-panel__thumb-count">+{previewModalUrls.length - 1}</span>
                    )}
                  </>
                ) : (
                  <span className="monitoring-sse-panel__thumb-placeholder">暂无截图</span>
                )}
              </div>
              <div className="monitoring-sse-panel__thumb-actions">
                <Button
                  type="link"
                  size="small"
                  className="monitoring-sse-panel__thumb-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    openLiveModal(liveParams)
                  }}
                >
                  看直播
                </Button>
                <Button
                  type="link"
                  size="small"
                  className="monitoring-sse-panel__thumb-btn"
                  disabled={!previewThumbSrc}
                  onClick={(e) => {
                    e.stopPropagation()
                    openSnapshotPreview({
                      urls: previewModalUrls,
                      streamKey: group.streamKey,
                      thumbUrl: previewThumbSrc
                    })
                  }}
                >
                  看截图
                </Button>
              </div>
            </div>
          )}
        </div>
      </List.Item>
    )
  }

  return (
    <>
      <Drawer
        title={`${monitoringDrawer?.titlePrefix ?? ''}（${monitoringDrawerItems.length}条）`}
        open={Boolean(monitoringDrawer)}
        onClose={closeMonitoringDrawer}
        width={800}
        footer={null}
        maskClosable
        destroyOnClose
        styles={{
          body: { overflowY: 'auto', overflowX: 'hidden' }
        }}
      >
        {monitoringDrawer ? (
          <NursingAlertsForm
            key={monitoringDrawer.streamKey}
            data={{}}
            query={monitoringServiceQuery}
            serviceType="monitoring"
            sseLocalItems={monitoringDrawerItems}
            deviceId={monitoringDrawer.deviceId}
            channelNo={monitoringDrawer.channelNo}
            monitoringStreamKey={monitoringDrawer.streamKey}
          />
        ) : null}
      </Drawer>
      <Modal
        key={snapshotPreview?.streamKey || snapshotPreview?.thumbUrl || 'snapshot'}
        title="预警截图"
        open={Boolean(snapshotPreview)}
        onCancel={closeSnapshotPreview}
        footer={null}
        width={Math.min(960, typeof window !== 'undefined' ? window.innerWidth - 32 : 960)}
        centered
        destroyOnClose
        className="monitoring-sse-snapshot-modal"
        styles={{ body: { paddingTop: 12, paddingBottom: 16, overflow: 'auto' } }}
      >
        {snapshotPreview?.urls?.map((url, idx) => (
          <img
            key={`${snapshotPreview.streamKey || 'snap'}-${url}-${idx}`}
            src={url}
            alt={`预警截图 ${idx + 1}`}
            className="monitoring-sse-snapshot-modal__img"
          />
        ))}
      </Modal>
      <Modal
        title="实时监控"
        open={Boolean(liveModal)}
        onCancel={closeLiveModal}
        footer={null}
        width={liveModalLayout.modalWidth}
        centered
        destroyOnClose
        className="monitoring-sse-live-modal"
        styles={{ body: { paddingTop: 12, paddingBottom: 16 } }}
      >
        {liveModal && ys7AccessToken ? (
          <div
            className="monitoring-sse-live-modal__player"
            style={{ height: liveModalLayout.playerHeight }}
          >
            <EzvizPlayer
              key={liveModal.containerId}
              containerId={liveModal.containerId}
              accessToken={ys7AccessToken}
              deviceSerial={liveModal.deviceSerial}
              channelNo={liveModal.channelNo}
              mode="live"
              clickToPlay={false}
              active
              wrapperStyle={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              }}
              containerClassName="monitoring-sse-panel__ezviz-mount"
              containerStyle={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                height: '100%'
              }}
            />
          </div>
        ) : null}
      </Modal>
      <div className="monitoring-sse-panel-body" style={{ position: 'relative', minHeight: httpInitLoading ? 120 : undefined }}>
        {httpInitLoading ? (
          <div
            className="monitoring-sse-panel__init-spin"
            style={{ padding: '24px 0', textAlign: 'center' }}
          >
            <Spin size="small" tip="正在加载未处理监控预警…" />
          </div>
        ) : (
          <List
            dataSource={pagedList}
            rowKey={(group) => group.streamKey}
            locale={{ emptyText: '暂无预警数据' }}
            className="alert-list"
            size="small"
            renderItem={renderGroupRow}
          />
        )}
      </div>
      {!httpInitLoading ? (
        <div className="pagination-container">
          <Pagination
            size="small"
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            showTotal={(t) => `共 ${t} 条（按设备+通道聚合后）`}
            hideOnSinglePage
            onChange={(p) => setPage(p)}
          />
        </div>
      ) : null}
    </>
  )
}

MonitoringSseAlertsPanel.propTypes = {
  monitoringGroupUnprocessed: PropTypes.shape({
    groups: PropTypes.array,
    loading: PropTypes.bool,
    loaded: PropTypes.bool,
    error: PropTypes.any
  }),
  monitoringAlarmSseMessages: PropTypes.array,
  monitoringAlarmProcessedStreamKeys: PropTypes.object,
  javaVirtualMessages: PropTypes.array,
  getStatusConfig: PropTypes.func.isRequired,
  monitoringServiceQuery: PropTypes.object,
  ys7AccessToken: PropTypes.string
}

MonitoringSseAlertsPanel.defaultProps = {
  monitoringGroupUnprocessed: undefined,
  monitoringAlarmSseMessages: [],
  monitoringAlarmProcessedStreamKeys: undefined,
  javaVirtualMessages: undefined,
  monitoringServiceQuery: {},
  ys7AccessToken: ''
}

export default MonitoringSseAlertsPanel
